import { ChangeEvent, FunctionComponent } from "react";

import { ERROR_TYPE, translateError } from "@/utils";

import { InputField } from "@/components/InputField/InputField";

interface LiSnUrlFieldProps {
	errorMessage?: unknown;
	handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
	label: string;
	name: string;
	placeholder: string;
	value: string;
}

/**
 * The sentence for a refused URL is the schema's own wording, not an `ERROR_TYPE`, so it must not
 * go through the error-code mapper that turns every unknown message into "Something went wrong"
 * (PREC-4510). A known code (e.g. `REQUIRED`) is still translated; anything else is shown as written.
 */
export const urlFieldMessage = (message: unknown): string | undefined => {
	if (typeof message !== "string" || !message) {
		return undefined;
	}

	const knownCode = Object.values(ERROR_TYPE).find((errorType) => errorType === message);

	return knownCode ? translateError[knownCode] : message;
};

/** The Sales Navigator URL input, with the schema's refusal sentence shown beside it. */
export const LiSnUrlField: FunctionComponent<LiSnUrlFieldProps> = ({
	errorMessage,
	handleChange,
	label,
	name,
	placeholder,
	value,
}) => {
	const message = urlFieldMessage(errorMessage);

	return (
		<div className="flex flex-col w-full mb-4">
			<InputField
				error={!!message}
				handleChange={handleChange}
				label={label}
				name={name}
				placeholder={placeholder}
				value={value}
			/>
			{message && (
				<p className="text-xs text-status-error" data-testid="li-sn-url-refusal" role="alert">
					{message}
				</p>
			)}
		</div>
	);
};
